import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { LongitudinalMetric, LongitudinalReport } from '../api/longitudinal';
import { prettyMetric } from '../util/metricLabels';

/** Compact number for the readouts — trims trailing zeros. */
function fmt(v: number | null): string {
  if (v === null) return '—';
  const r = Math.abs(v) >= 100 ? Math.round(v) : Number(v.toFixed(2));
  return String(r);
}

/** `v` to 3 significant figures — a slope of 0.0031 per session must not
 * round to 0. Matches the PDF report's `_sig`. */
function sig(v: number): string {
  if (v === 0 || !Number.isFinite(v)) return '0';
  return String(Number(v.toPrecision(3)));
}

/**
 * The trend line's statistics in one line (LONGITUDINAL-TREND), worded as in
 * the PDF report: slope per session with its 95 % CI, R², p and n.
 */
export function trendStatsText(m: LongitudinalMetric): string {
  const f = m.trend_fit;
  if (!f) return 'Too few sessions for a trend line (needs 3).';
  const unit = m.unit ? ` ${m.unit}` : '';
  const p = f.p_value < 0.001 ? 'p < 0.001' : `p = ${f.p_value.toFixed(3)}`;
  return (
    `Trend ${f.slope > 0 ? '+' : ''}${sig(f.slope)}${unit} per session ` +
    `(95% CI ${sig(f.slope_ci_low)} to ${sig(f.slope_ci_high)}) · ` +
    `R² ${f.r_squared.toFixed(2)} · ${p} · n = ${f.n}`
  );
}

/** Short day label (e.g. "Aug 1") for a session's timeline axis tick. */
function fmtDay(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function TrendChip({ m }: { m: LongitudinalMetric }) {
  if (m.trend === 'improving') {
    return (
      <Chip size="small" color="success" icon={<TrendingUpIcon />}
        label={`Improving${m.change_pct != null ? ` +${Math.abs(m.change_pct)}%` : ''}`} />
    );
  }
  if (m.trend === 'declining') {
    return (
      <Chip size="small" color="error" icon={<TrendingDownIcon />}
        label={`Declining ${m.change_pct != null ? `${Math.abs(m.change_pct)}%` : ''}`} />
    );
  }
  if (m.trend === 'flat') {
    return <Chip size="small" icon={<TrendingFlatIcon />} label="No clear trend" />;
  }
  return <Chip size="small" variant="outlined" label="Relative signal" />;
}

function MetricRow({ m }: { m: LongitudinalMetric }) {
  // The sessions as dots plus the least-squares line and its 95 % band —
  // no line joining one session to the next.
  const f = m.trend_fit;
  const data = m.points.map((p, i) => ({
    i,
    value: p.value,
    fit: f?.fitted[i],
    band: f ? [f.band_low[i], f.band_high[i]] : undefined,
  }));
  const stroke =
    m.trend === 'improving'
      ? '#2e7d32'
      : m.trend === 'declining'
        ? '#c62828'
        : '#78909c';
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '220px 1fr 260px' },
        alignItems: 'center',
        gap: 2,
        py: 1.25,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <Box>
        <Typography variant="body2" fontWeight={600}>
          {prettyMetric(m.key)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {fmt(m.first)} → {fmt(m.latest)}
          {m.unit ? ` ${m.unit}` : ''} · {m.n} session{m.n === 1 ? '' : 's'}
        </Typography>
      </Box>

      {/* The wide progression chart — full column width. */}
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ height: 72 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 6, bottom: 6, left: 0, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.25} />
              <XAxis dataKey="i" hide />
              <YAxis hide domain={['dataMin', 'dataMax']} />
              <Tooltip
                formatter={(v, name) =>
                  name === 'band' && Array.isArray(v)
                    ? [`${fmt(Number(v[0]))} – ${fmt(Number(v[1]))}`, '95% band']
                    : name === 'fit'
                      ? [fmt(Number(v)), 'Trend line']
                      : [fmt(Number(v)), prettyMetric(m.key)]
                }
                labelFormatter={(i) => `Session ${Number(i) + 1}`}
              />
              {f && (
                <Area
                  dataKey="band"
                  stroke="none"
                  fill={stroke}
                  fillOpacity={0.12}
                  isAnimationActive={false}
                />
              )}
              {f && (
                <Line
                  dataKey="fit"
                  stroke={stroke}
                  strokeWidth={2}
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                />
              )}
              <Line
                dataKey="value"
                stroke="none"
                dot={{ r: 2.5, fill: '#fff', stroke, strokeWidth: 1.5 }}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
        <Typography variant="caption" color="text.secondary" component="div">
          {trendStatsText(m)}
        </Typography>
      </Box>

      <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
        <TrendChip m={m} />
        {m.tier && (
          <Chip
            size="small"
            variant="outlined"
            label={`Now: ${m.tier}`}
            color={m.tier === 'bad' ? 'default' : 'primary'}
          />
        )}
        {m.indicative && (
          <Chip size="small" variant="outlined" color="warning" label="Indicative" />
        )}
      </Stack>
    </Box>
  );
}

/**
 * Presentational longitudinal report (LONGITUDINAL-ANALYTICS): each configured
 * metric mapped across the participant's sessions of a procedure, in time
 * order, each with its least-squares trend line and statistics. Rendered wide — designed to take over
 * the scorecard panel. `Indicative` = a soft read (few sessions or a
 * relative-only signal).
 */
export function LongitudinalReportView({
  report,
}: {
  report: LongitudinalReport;
}) {
  if (report.session_count === 0 || report.metrics.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No scored sessions of this procedure for this participant yet.
      </Typography>
    );
  }
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {report.session_count} session{report.session_count === 1 ? '' : 's'} ·
        each metric across the sessions in time order: dots are sessions, the
        line is the least-squares trend with its 95% band. A trend counts only
        when p &lt; 0.05. <em>Indicative</em> = few sessions or a relative-only
        signal.
      </Typography>
      <Box sx={{ mt: 1 }}>
        {report.metrics.map((m) => (
          <MetricRow key={m.key} m={m} />
        ))}
      </Box>
    </Box>
  );
}

// Line colour per participant — base owner first, then the compare picks.
const SERIES_COLORS = [
  '#1565c0',
  '#2e7d32',
  '#c62828',
  '#6a1b9a',
  '#ef6c00',
  '#00838f',
  '#5d4037',
];

type CompareSeries = { key: string; label: string; color: string };

/**
 * Compare progression across participants (LONGITUDINAL-COMPARE): the SAME
 * per-metric layout as the single-participant view, but every metric's chart
 * overlays every participant — the base owner plus each compare pick — as
 * their sessions (dots) and their own least-squares trend line, so you read
 * each metric (Focus, …) for all participants on one chart. `reports[0]` is
 * the base participant the overlay opened on. A participant with fewer
 * sessions simply plots the sessions they have; their line ends where their
 * data does.
 */
export function LongitudinalCompareView({
  reports,
}: {
  reports: LongitudinalReport[];
}) {
  const series: CompareSeries[] = reports.map((r, i) => ({
    key: `s${i}`,
    label: r.participant_label ?? 'Participant',
    color: SERIES_COLORS[i % SERIES_COLORS.length],
  }));
  // Metric order: the base report first, then any metric only others have.
  const order: string[] = [];
  const seen = new Set<string>();
  for (const r of reports)
    for (const m of r.metrics)
      if (!seen.has(m.key)) {
        seen.add(m.key);
        order.push(m.key);
      }
  const byKey = reports.map((r) => new Map(r.metrics.map((m) => [m.key, m])));
  return (
    <Box>
      {/* Participant legend — colour ↔ name for every line below. */}
      <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
        {series.map((s) => (
          <Stack key={s.key} direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 16, height: 3, bgcolor: s.color, borderRadius: 1 }} />
            <Typography variant="caption" fontWeight={600}>
              {s.label}
            </Typography>
          </Stack>
        ))}
      </Stack>
      {order.map((key) => (
        <CompareMetricRow
          key={key}
          metricKey={key}
          series={series}
          metrics={byKey.map((map) => map.get(key))}
        />
      ))}
    </Box>
  );
}

function CompareMetricRow({
  metricKey,
  series,
  metrics,
}: {
  metricKey: string;
  series: CompareSeries[];
  metrics: (LongitudinalMetric | undefined)[];
}) {
  const maxLen = Math.max(0, ...metrics.map((m) => m?.points.length ?? 0));
  const unit = metrics.find((m) => m?.unit)?.unit ?? null;
  // The base owner (series[0]) anchors the timeline; label the axis ends with
  // their first/last session date so the chart reads as a dated progression.
  const ownerDates = metrics[0]?.points.map((p) => p.started_at) ?? [];
  // One row per session index; a participant contributes only the sessions
  // they actually have, so shorter series stop early.
  const data = Array.from({ length: maxLen }, (_, i) => {
    const row: Record<string, number> = { i };
    metrics.forEach((m, pi) => {
      const v = m?.points[i]?.value;
      if (v != null) row[series[pi].key] = v;
      const fit = m?.trend_fit?.fitted[i];
      if (fit != null) row[`${series[pi].key}_fit`] = fit;
    });
    return row;
  });
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '220px 1fr 240px' },
        alignItems: 'center',
        gap: 2,
        py: 1.25,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <Box>
        <Typography variant="body2" fontWeight={600}>
          {prettyMetric(metricKey)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          over sessions{unit ? ` · ${unit}` : ''}
        </Typography>
      </Box>
      <Box sx={{ height: 96, minWidth: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 6, bottom: 6, left: 0, right: 8 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.25} />
            <XAxis
              dataKey="i"
              interval="preserveStartEnd"
              tickFormatter={(i) => fmtDay(ownerDates[Number(i)]) || `#${Number(i) + 1}`}
              fontSize={10}
              tickLine={false}
            />
            <YAxis hide domain={['dataMin', 'dataMax']} />
            <Tooltip
              formatter={(v, name) => [fmt(Number(v)), String(name)]}
              filterNull
              labelFormatter={(i) =>
                fmtDay(ownerDates[Number(i)])
                  ? `Session #${Number(i) + 1} · ${fmtDay(ownerDates[Number(i)])}`
                  : `Session #${Number(i) + 1}`
              }
            />
            {/* Each participant: their sessions as dots and their own
                least-squares trend line (from 3 sessions on). */}
            {series.map((s, pi) =>
              metrics[pi]?.trend_fit ? (
                <Line
                  key={`${s.key}_fit`}
                  dataKey={`${s.key}_fit`}
                  name={`${s.label} · trend`}
                  stroke={s.color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                />
              ) : null,
            )}
            {series.map((s) => (
              <Line
                key={s.key}
                dataKey={s.key}
                name={s.label}
                stroke="none"
                dot={{ r: 2.5, fill: s.color, stroke: s.color }}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>

      {/* Per-participant trend read — colour dot ties each verdict to its line. */}
      <Stack spacing={0.5}>
        {series.map((s, pi) => {
          const m = metrics[pi];
          if (!m) return null;
          return (
            <Stack key={s.key} direction="row" spacing={0.75} alignItems="center">
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: s.color,
                  flexShrink: 0,
                }}
              />
              <TrendChip m={m} />
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}
